/**
 * Guards a protected area: while auth state is loading shows a spinner; when
 * unauthenticated renders the `fallback` (the sign-in screen); when
 * authenticated renders `children`.
 *
 * This is the SPA equivalent of a router redirect — the unauthenticated user
 * never sees protected content.
 */

import type { ReactNode } from "react";
import { useAuth } from "./AuthContext.js";

export interface ProtectedRouteProps {
  children: ReactNode;
  /** Rendered when the user is not signed in (e.g. <SignInForm />). */
  fallback: ReactNode;
}

export function ProtectedRoute({ children, fallback }: ProtectedRouteProps): JSX.Element {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <p
        role="status"
        aria-live="polite"
        className="flex items-center justify-center gap-3 py-16 text-sm text-zinc-400"
      >
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-700 border-t-blue-400"
        />
        Cargando…
      </p>
    );
  }

  if (!user) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
